import { createTheme, alpha } from "@mui/material/styles";

const INDIGO = "#5B5BD6";
const INDIGO_DARK = "#4747B8";
const INDIGO_LIGHT = "#8181E5";
const INDIGO_SUBTLE = alpha(INDIGO, 0.08);

const theme = createTheme({
  palette: {
    mode: "light",
    primary: { main: INDIGO, dark: INDIGO_DARK, light: INDIGO_LIGHT, contrastText: "#fff" },
    secondary: { main: "#0EA5E9", contrastText: "#fff" },
    success: { main: "#10B981", light: "#D1FAE5", contrastText: "#fff" },
    warning: { main: "#F59E0B", light: "#FEF3C7", contrastText: "#fff" },
    error: { main: "#EF4444", light: "#FEE2E2", contrastText: "#fff" },
    background: { default: "#F0F2F7", paper: "#FFFFFF" },
    text: { primary: "#0F172A", secondary: "#64748B" },
    divider: "#E8EDF5",
    grey: {
      50: "#F8FAFC",
      100: "#F1F5F9",
      200: "#E2E8F0",
      300: "#CBD5E1",
      400: "#94A3B8",
      500: "#64748B",
      600: "#475569",
      700: "#334155",
      800: "#1E293B",
      900: "#0F172A",
    },
  },
  shape: { borderRadius: 10 },
  shadows: [
    "none",
    "0px 1px 2px rgba(15,23,42,0.06)",
    "0px 1px 4px rgba(15,23,42,0.08)",
    "0px 2px 8px rgba(15,23,42,0.08)",
    "0px 4px 16px rgba(15,23,42,0.08)",
    "0px 8px 24px rgba(15,23,42,0.10)",
    "0px 12px 32px rgba(15,23,42,0.12)",
    "0px 16px 40px rgba(15,23,42,0.14)",
    "0px 20px 48px rgba(15,23,42,0.16)",
    ...Array(16).fill("none"),
  ],
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 700, fontSize: "2rem", letterSpacing: "-0.025em", lineHeight: 1.2 },
    h2: { fontWeight: 700, fontSize: "1.5rem", letterSpacing: "-0.02em", lineHeight: 1.25 },
    h3: { fontWeight: 600, fontSize: "1.125rem", letterSpacing: "-0.01em", lineHeight: 1.35 },
    h4: { fontWeight: 600, fontSize: "1rem", lineHeight: 1.4 },
    h5: { fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.4 },
    h6: { fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.4 },
    subtitle1: { fontWeight: 600, fontSize: "0.9375rem" },
    subtitle2: { fontWeight: 600, fontSize: "0.875rem", color: "#64748B" },
    body1: { fontSize: "0.9375rem", lineHeight: 1.6 },
    body2: { fontSize: "0.875rem", lineHeight: 1.55 },
    caption: { fontSize: "0.775rem", lineHeight: 1.5, color: "#64748B" },
    button: { textTransform: "none", fontWeight: 600, fontSize: "0.875rem" },
    overline: {
      fontSize: "0.675rem",
      letterSpacing: "0.09em",
      fontWeight: 700,
      textTransform: "uppercase",
      color: "#94A3B8",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*, *::before, *::after": { boxSizing: "border-box" },
        html: { scrollBehavior: "smooth" },
        body: { overscrollBehavior: "none" },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid #E8EDF5",
          borderRadius: 12,
        },
        elevation1: { boxShadow: "0px 1px 3px rgba(15,23,42,0.06), 0px 1px 2px rgba(15,23,42,0.04)" },
        elevation2: { boxShadow: "0px 4px 12px rgba(15,23,42,0.08)" },
        elevation3: { boxShadow: "0px 8px 24px rgba(15,23,42,0.10)" },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border: "1px solid #E8EDF5",
          borderRadius: 12,
          overflow: "hidden",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          "&:last-child": { paddingBottom: 20 },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, paddingInline: 14, paddingBlock: 7, lineHeight: 1.5 },
        sizeSmall: { paddingInline: 10, paddingBlock: 5, fontSize: "0.8125rem" },
        sizeLarge: { paddingInline: 20, paddingBlock: 10, fontSize: "0.9375rem" },
        contained: {
          boxShadow: "0px 1px 2px rgba(91,91,214,0.25), 0px 0px 0px 1px rgba(91,91,214,0.2)",
          "&:hover": { boxShadow: "0px 2px 6px rgba(91,91,214,0.30)" },
        },
        outlined: {
          borderColor: "#D1D9E6",
          "&:hover": { borderColor: INDIGO, backgroundColor: INDIGO_SUBTLE },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "&:hover": { backgroundColor: "#F1F5F9" },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: "0.75rem", borderRadius: 6 },
        sizeSmall: { height: 22, fontSize: "0.7rem" },
      },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "inherit" },
      styleOverrides: {
        root: {
          borderBottom: "1px solid #E8EDF5",
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(8px)",
          zIndex: 1200,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: "56px !important", paddingInline: "20px !important" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: "none",
          backgroundColor: "#1E293B",
          backgroundImage: "none",
          color: "#CBD5E1",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          marginInline: 8,
          marginBlock: 1,
          color: "#94A3B8",
          paddingBlock: 8,
          transition: "all 0.15s ease",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,0.06)",
            color: "#E2E8F0",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(91,91,214,0.18)",
            color: "#A5B4FC",
            "&:hover": { backgroundColor: "rgba(91,91,214,0.22)" },
          },
          "&.Mui-disabled": {
            opacity: 0.4,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 32, color: "inherit" },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: "#F8FAFC" },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: "#E8EDF5", padding: "10px 12px" },
        head: {
          fontWeight: 700,
          color: "#64748B",
          fontSize: "0.72rem",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          padding: "8px 12px",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
        },
      },
    },
    MuiTextField: {
      defaultProps: { size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            "& fieldset": { borderColor: "#D1D9E6" },
            "&:hover fieldset": { borderColor: "#94A3B8" },
            "&.Mui-focused fieldset": { borderColor: INDIGO },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#D1D9E6" },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: "#1E293B",
          fontSize: "0.75rem",
          borderRadius: 6,
          padding: "5px 10px",
        },
        arrow: { color: "#1E293B" },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 },
        standardInfo: { backgroundColor: "#EFF6FF", border: "1px solid #BFDBFE" },
        standardSuccess: { backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" },
        standardWarning: { backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" },
        standardError: { backgroundColor: "#FFF1F2", border: "1px solid #FECDD3" },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: "#E8EDF5" } },
    },
    MuiSkeleton: {
      defaultProps: { animation: "wave" },
      styleOverrides: { root: { borderRadius: 8 } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700 },
        rounded: { borderRadius: 10 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, border: "1px solid #E8EDF5" },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 2 },
        root: { minHeight: 44 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          fontWeight: 600,
          fontSize: "0.85rem",
          textTransform: "none",
          color: "#64748B",
          "&.Mui-selected": { color: INDIGO },
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: { bottom: 24, right: 24 },
      },
    },
  },
});

export default theme;
export { INDIGO, INDIGO_DARK, INDIGO_LIGHT, INDIGO_SUBTLE };
